import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  TextField,
  Typography,
  Box,
  Stack,
  Avatar,
  Divider,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useDispatch, useSelector } from "react-redux";
import { uploadTransactionImages } from "../../redux/slices/transpicture";

const DepositPaymentModal = ({ open, onClose, onConfirm }) => {
  const { data: paymentMethods, loading: paymentsLoading } = useSelector(
    (state) => state.paymentmethod,
  );
  const { loading: savingDeposit } = useSelector((u) => u?.transpic);

  const [selectedMethod, setSelectedMethod] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  // Reset all state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedMethod(null);
      setUploading(false);
      setScreenshot(null);
      setCopied(false);
      setErrors({});
    } else if (paymentMethods.length > 0) {
      setSelectedMethod(paymentMethods[0]);
    }
  }, [open, paymentMethods]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 4 * 1024 * 1024; // 4MB

    if (!allowedTypes.includes(file.type)) {
      return alert("Only JPG, PNG, or WEBP images are allowed.");
    }
    if (file.size > maxSize) {
      return alert("File is too large. Max size is 4MB.");
    }

    setUploading(true);
    const image = await dispatch(uploadTransactionImages(file));
    if (image) {
      setScreenshot(image);
      setErrors((prev) => ({ ...prev, screenshot: "" }));
    }
    setUploading(false);
  };

  const handleRemoveImage = () => {
    setScreenshot(null);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500); // hide after 1.5s
  };

  const handleConfirm = () => {
    const newErrors = {};

    if (!selectedMethod) {
      newErrors.method = "Please select a payment method.";
      setErrors(newErrors);
      return;
    }

    // For other payment methods, require screenshot
    if (!screenshot && selectedMethod.type !== "card") {
      newErrors.screenshot = "Uploading proof is required.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    onConfirm({
      method: selectedMethod?.type,
      attach: screenshot,
    });
  };

  const isCardPayment = selectedMethod?.type === "card";

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          onClose();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 500,
            textAlign: "left",
            color: "text.primary",
          }}
        >
          Deposit Payment
        </DialogTitle>
        <Divider />

        <DialogContent>
          {/* Dropdown */}
          <TextField
            select
            fullWidth
            label="Select Payment Method"
            value={selectedMethod?.type || ""}
            onChange={(e) => {
              const method = paymentMethods.find(
                (pm) => pm.type === e.target.value,
              );
              setSelectedMethod(method);
              setErrors((prev) => ({ ...prev, method: "" }));
            }}
            disabled={paymentsLoading}
            sx={{
              mt: 2,
              "& .MuiInputBase-input": { color: "text.primary" },
              "& .MuiInputLabel-root": { color: "text.primary" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
            }}
            error={!!errors.method}
            helperText={errors.method}
          >
            {paymentsLoading ? (
              <MenuItem disabled>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{ width: "100%" }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading payment methods...
                  </Typography>
                </Stack>
              </MenuItem>
            ) : paymentMethods.length > 0 ? (
              paymentMethods.map((pm) => (
                <MenuItem key={pm.type} value={pm.type}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      src={pm.icon}
                      alt={pm.type}
                      variant="rounded"
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        "& img": {
                          objectFit: "contain",
                        },
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: "text.primary",
                        textTransform: "capitalize",
                      }}
                    >
                      {pm.type}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No payment methods available</MenuItem>
            )}
          </TextField>

          {/* Payment Info */}
          {selectedMethod && (
            <>
              {isCardPayment ? (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    textAlign: "center",
                    bgcolor: "background.default",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                  >
                    Deposit via Gateway
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Click "Proceed to Gateway" below to complete your payment
                    securely.
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    You will be redirected to our secure payment gateway to
                    complete the transaction.
                  </Typography>
                </Paper>
              ) : (
                <>
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "background.default",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      Pay To:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      {selectedMethod.accountName}
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        mt: 1,
                        bgcolor: "action.hover",
                        p: 1,
                        borderRadius: 1,
                        wordBreak: "break-all",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ flex: 1 }}
                      >
                        {selectedMethod.accountValue}
                      </Typography>
                      <Tooltip
                        title="Copied!"
                        open={copied}
                        placement="top"
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            handleCopy(selectedMethod.accountValue || "")
                          }
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Transfer the amount to the account above, then upload a
                      screenshot or payment receipt below so we can confirm your
                      deposit.
                    </Typography>
                  </Paper>

                  {/* Upload Screenshot Section - Only for non-card payments */}
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      border: "2px dashed",
                      borderColor: errors.screenshot ? "error.main" : "divider",
                      borderRadius: 2,
                      textAlign: "center",
                      position: "relative",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    onClick={
                      !screenshot
                        ? () => document.getElementById("file-upload").click()
                        : undefined
                    }
                  >
                    {uploading ? (
                      <CircularProgress size={28} />
                    ) : screenshot ? (
                      <Box
                        sx={{
                          position: "relative",
                          maxHeight: 200,
                          overflow: "hidden",
                          borderRadius: 2,
                        }}
                      >
                        <img
                          src={screenshot}
                          alt="screenshot"
                          style={{
                            width: "100%",
                            maxHeight: "250px",
                            objectFit: "contain",
                            borderRadius: 8,
                            display: "block",
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "white",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Stack alignItems="center" spacing={1}>
                        <CloudUploadIcon color="primary" fontSize="large" />
                        <Typography variant="body2" color="text.secondary">
                          Click to upload deposit screenshot
                        </Typography>
                      </Stack>
                    )}
                    <input
                      type="file"
                      id="file-upload"
                      accept="image/*"
                      hidden
                      onChange={handleFileUpload}
                    />
                  </Box>
                  {errors.screenshot && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {errors.screenshot}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            fullWidth
            disabled={savingDeposit}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            fullWidth
            disabled={savingDeposit}
            endIcon={isCardPayment ? <OpenInNewIcon /> : null}
          >
            {savingDeposit ? (
              <CircularProgress size={22} color="inherit" />
            ) : isCardPayment ? (
              "Proceed"
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DepositPaymentModal;
